// UserManagement actions renderer

import type { User } from '@/api/types'
import type { Role, TenantOption } from '@pages/Users/UserManagement/types'
import { Button, Popover, Select, Space } from 'antd'
import React, { useMemo, useState } from 'react'
import { formatRoleName } from '../utils'

interface ActionsRendererProps {
  data: User
  onUpdateAccess: (user: User, tenantName: string, roles: string[]) => void
  onCreateApiKey: (user: User) => void
  onSendPasswordResetLink: (user: User) => void
  availableTenants: TenantOption[]
  availableRoles: Role[] | undefined
}

export const ActionsRenderer: React.FC<ActionsRendererProps> = ({
  data: user,
  onUpdateAccess,
  onCreateApiKey,
  onSendPasswordResetLink,
  availableTenants,
  availableRoles,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<string | undefined>()
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])

  const tenantOptions = useMemo(
    () =>
      availableTenants.map((tenant) => ({
        label: tenant.name,
        value: tenant.name,
      })),
    [availableTenants],
  )

  const roleOptions = useMemo(() => {
    if (!availableRoles) return []
    return availableRoles.map((role) => ({
      label: formatRoleName(role.name),
      value: role._id,
    }))
  }, [availableRoles])

  const initializeSelections = () => {
    const gravitateDomain = user.domains?.find(
      (domain) => domain.tenant_name === 'Gravitate',
    )
    const firstDomain = user.domains?.[0]

    const defaultTenant =
      gravitateDomain?.tenant_name ||
      firstDomain?.tenant_name ||
      tenantOptions[0]?.value

    const candidateRoles = [
      ...(gravitateDomain?.roles || []),
      ...(firstDomain?.roles || []),
    ]

    const matchedRoleIds = Array.from(
      new Set(
        candidateRoles
          .map((roleId) => {
            if (!roleId || !availableRoles) {
              return undefined
            }
            const lower = roleId.toLowerCase()
            return availableRoles.find(
              (availableRole) =>
                availableRole._id === roleId ||
                availableRole.name === roleId ||
                availableRole.name?.toLowerCase() === lower,
            )?._id
          })
          .filter((roleId): roleId is string => Boolean(roleId)),
      ),
    )

    if (defaultTenant) {
      setSelectedTenant(defaultTenant)
    } else {
      setSelectedTenant(undefined)
    }
    setSelectedRoles(matchedRoleIds)
  }

  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open)
    if (open) {
      initializeSelections()
    }
  }

  const handleSave = () => {
    if (!selectedTenant || selectedRoles.length === 0) return
    onUpdateAccess(user, selectedTenant, selectedRoles)
    setPopoverOpen(false)
  }

  return (
    <div className='flex items-center justify-end w-full'>
      <Button type='link' size='small' onClick={() => onCreateApiKey(user)}>
        Create API Key
      </Button>
      <Button
        type='link'
        size='small'
        disabled={!user.email}
        onClick={() => onSendPasswordResetLink(user)}
      >
        Send Password Reset
      </Button>
      <Popover
        open={popoverOpen}
        onOpenChange={handleOpenChange}
        trigger='click'
        placement='left'
        content={
          <Space direction='vertical' size='small'>
            <Select
              placeholder='Select tenant'
              showSearch
              optionFilterProp='label'
              value={selectedTenant}
              onChange={setSelectedTenant}
              options={tenantOptions}
              style={{ width: 200 }}
              disabled={tenantOptions.length === 0}
            />
            <Select
              mode='multiple'
              placeholder='Select roles'
              value={selectedRoles}
              onChange={setSelectedRoles}
              options={roleOptions}
              style={{ width: 200 }}
              disabled={roleOptions.length === 0}
            />
            <Button
              type='primary'
              size='small'
              onClick={handleSave}
              disabled={!selectedTenant || selectedRoles.length === 0}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Button
          type='link'
          size='small'
          disabled={tenantOptions.length === 0 || roleOptions.length === 0}
        >
          Manage Access
        </Button>
      </Popover>
    </div>
  )
}
